import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import AdminStaffDetail from "./AdminStaffDetail";
import { useConfirmModal, getAdminSession } from "./adminShared";

const fmtDateTime = (v) => {
    if (!v) return "Nunca acessou";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const isClockedInToday = (staff) => {
    const today = todayISO();
    return (staff.time_records || []).some((r) => r.date === today && r.clock_in && !r.clock_out);
};

const permCount = (staff) =>
    Object.values(staff.permissions || {}).filter((p) => p?.view).length;

/**
 * AdminStaff — módulo "Funcionários" (super-admin). Lista + KPIs; clicar
 * numa linha abre AdminStaffDetail (também usado pra criação, quando
 * detailId === "new").
 */
const AdminStaff = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [detailId, setDetailId] = useState(null);
    const { requestConfirm, ConfirmModalElement } = useConfirmModal();
    const { isSuperAdmin, permissions } = getAdminSession();
    const canEdit = isSuperAdmin || !!permissions?.staff?.edit;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setItems(await api.get("/admin/staff") || []);
        } catch (e) {
            toast.error("Erro ao carregar funcionários", { description: e.message });
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const kpis = {
        total: items.length,
        active: items.filter((s) => s.active).length,
        clockedIn: items.filter(isClockedInToday).length,
    };

    const handleDeactivate = (s) => {
        requestConfirm({
            title: "Excluir funcionário",
            message: `Tem certeza que deseja excluir ${s.name}? O acesso é revogado imediatamente (o registro é mantido para histórico/auditoria).`,
            confirmLabel: "Excluir",
            danger: true,
            onConfirm: async () => {
                try {
                    await api.del(`/admin/staff/${s.id}`);
                    toast.success("Funcionário excluído");
                    load();
                } catch (e) {
                    toast.error("Falha ao excluir", { description: e.message });
                }
            },
        });
    };

    const handleCleanupDemo = () => {
        requestConfirm({
            title: "Limpar dados de demonstração",
            message: "Isso remove definitivamente os funcionários de demonstração (marina@kinng.app e rafael@kinng.app) da base de dados. Esta ação não pode ser desfeita.",
            confirmLabel: "Limpar",
            danger: true,
            onConfirm: async () => {
                try {
                    const res = await api.post("/admin/staff/cleanup-demo", {});
                    if (res?.removed_count > 0) {
                        toast.success(`${res.removed_count} funcionário(s) de demonstração removido(s)`);
                        load();
                    } else {
                        toast.info("Nenhum dado de demonstração encontrado");
                    }
                } catch (e) {
                    toast.error("Falha ao limpar dados de demonstração", { description: e.message });
                }
            },
        });
    };

    if (detailId) {
        return (
            <AdminStaffDetail
                staffId={detailId === "new" ? null : detailId}
                onBack={() => { setDetailId(null); load(); }}
            />
        );
    }

    return (
        <div className="space-y-6" data-testid="admin-staff-view">
            <header className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-indigo-400">Plataforma</div>
                    <h1 className="salon-heading text-3xl text-white mt-1">Funcionários</h1>
                    <p className="text-sm text-slate-400 mt-1">
                        Equipe interna Kinng.app — acesso ao painel mestre, permissões e ponto.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {isSuperAdmin && (
                        <button
                            data-testid="admin-staff-cleanup-demo"
                            onClick={handleCleanupDemo}
                            title="Remove os funcionários de demonstração (marina@kinng.app, rafael@kinng.app)"
                            className="min-h-[44px] px-4 rounded-xl border border-white/10 hover:border-red-500/40 text-slate-300 hover:text-red-300 font-semibold text-xs uppercase tracking-wider transition-all"
                        >
                            <i className="fa-solid fa-broom mr-2"></i>Limpar dados de demonstração
                        </button>
                    )}
                    {canEdit && (
                        <button
                            data-testid="admin-staff-new"
                            onClick={() => setDetailId("new")}
                            className="min-h-[44px] px-4 rounded-xl bg-indigo-500 hover:bg-indigo-400 text-white font-semibold text-xs uppercase tracking-wider transition-all"
                        >
                            <i className="fa-solid fa-user-plus mr-2"></i>Novo Funcionário
                        </button>
                    )}
                </div>
            </header>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <StaffKpi label="Total de Funcionários" value={kpis.total} icon="fa-users" />
                <StaffKpi label="Ativos" value={kpis.active} icon="fa-user-check" color="#10b981" />
                <StaffKpi label="Em Ponto agora" value={kpis.clockedIn} icon="fa-clock" color="#f59e0b" />
            </div>

            <div className="rounded-2xl border border-white/5 bg-slate-900/40 backdrop-blur-xl overflow-hidden" data-testid="admin-staff-table">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-[0.25em] text-slate-500 bg-slate-950/60">
                                <th className="text-left py-3 px-4">Funcionário</th>
                                <th className="text-left py-3 px-4">Cargo</th>
                                <th className="text-center py-3 px-4">Status</th>
                                <th className="text-center py-3 px-4">Permissões</th>
                                <th className="text-left py-3 px-4">Último acesso</th>
                                {isSuperAdmin && <th className="text-right py-3 px-4">Ações</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={6} className="text-center py-12 text-xs text-slate-500">Carregando…</td></tr>
                            ) : items.length === 0 ? (
                                <tr><td colSpan={6} className="text-center py-12 text-xs text-slate-500">Nenhum funcionário cadastrado.</td></tr>
                            ) : items.map((s) => (
                                <tr
                                    key={s.id}
                                    data-testid={`admin-staff-row-${s.id}`}
                                    onClick={() => setDetailId(s.id)}
                                    className="border-t border-white/5 hover:bg-white/5 transition-colors cursor-pointer"
                                >
                                    <td className="py-3 px-4">
                                        <div className="flex items-center gap-3">
                                            {s.avatar_url ? (
                                                <img src={s.avatar_url} alt="" className="h-9 w-9 rounded-full object-cover shrink-0" />
                                            ) : (
                                                <div
                                                    className="h-9 w-9 rounded-full flex items-center justify-center text-white font-bold text-xs shrink-0"
                                                    style={{ background: `hsl(${(s.name.charCodeAt(0) * 17) % 360}, 60%, 45%)` }}
                                                >
                                                    {s.name.slice(0, 2).toUpperCase()}
                                                </div>
                                            )}
                                            <div className="min-w-0">
                                                <div className="text-white font-medium truncate">{s.name}</div>
                                                <div className="text-[11px] text-slate-500 truncate">{s.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="py-3 px-4 text-slate-300">{s.cargo || "—"}</td>
                                    <td className="py-3 px-4 text-center">
                                        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold border ${
                                            s.active ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-slate-500/15 text-slate-400 border-slate-500/30"
                                        }`}>
                                            <span className={`h-1.5 w-1.5 rounded-full ${s.active ? "bg-emerald-400 animate-pulse" : "bg-slate-500"}`}></span>
                                            {s.active ? "Ativo" : "Inativo"}
                                        </span>
                                    </td>
                                    <td className="py-3 px-4 text-center font-mono text-slate-300">{permCount(s)}/8</td>
                                    <td className="py-3 px-4 text-xs text-slate-400">{fmtDateTime(s.last_login)}</td>
                                    {isSuperAdmin && (
                                        <td className="py-3 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                                            {s.active && (
                                                <button
                                                    data-testid={`admin-staff-delete-${s.id}`}
                                                    onClick={() => handleDeactivate(s)}
                                                    title="Excluir"
                                                    className="h-8 w-8 rounded-lg bg-white/5 hover:bg-red-500/20 hover:text-red-400 text-slate-400 transition-all"
                                                >
                                                    <i className="fa-solid fa-trash text-xs"></i>
                                                </button>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {ConfirmModalElement}
        </div>
    );
};

const StaffKpi = ({ label, value, icon, color = "#6366f1" }) => (
    <div className="rounded-2xl border border-white/5 bg-slate-900/40 backdrop-blur-xl p-4">
        <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">{label}</div>
            <div className="h-8 w-8 rounded-lg flex items-center justify-center text-sm" style={{ background: `${color}22`, color }}>
                <i className={`fa-solid ${icon}`}></i>
            </div>
        </div>
        <div className="salon-heading text-2xl text-white mt-2">{value}</div>
    </div>
);

export default AdminStaff;
